import { c as _c } from "react/compiler-runtime";
import { MarkdownText } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Components } from 'react-markdown';
import rehypeReferenceChips from '../../prompt-mention/rehype-reference-chips';
import useReferenceIndex from '../../prompt-mention/use-reference-index';
import instructionFencePreview from './instruction-fence-preview';
type ProsePreviewProps = {
  value: string;
  placeholder: string;
  /** Read-only previews show the full prose — there's no editor to open, so collapsing is pointless. */
  canEdit?: boolean;
  /** Task summary uses 'large' to read a step above the per-field prompts below it. */
  size?: 'default' | 'large';
};

// Longer previews clamp to ~10 lines with a Show more/less toggle so a prompt doesn't dominate the canvas.
const COLLAPSED_MAX_HEIGHT = 240;

// Fades the last clamped lines into the background instead of slicing them mid-glyph.
const FADE_MASK = 'linear-gradient(to bottom, #000 calc(100% - 48px), transparent)';

// Prose fields are stored as markdown (NotionEditor and Percy both emit markdown). Shrink the headings so a
// summary doesn't tower over the dense canvas; everything else uses MarkdownText's defaults.
const markdownComponents: Components = {
  h1: ({
    children
  }) => <h1 className="text-heading-3">{children}</h1>,
  h2: ({
    children
  }) => <h2 className="text-label-1">{children}</h2>,
  h3: ({
    children
  }) => <h3 className="text-label-2">{children}</h3>
};
const ProsePreview = t0 => {
  const $ = _c(40);
  const {
    value,
    placeholder,
    canEdit: t1,
    size: t2
  } = t0;
  const canEdit = t1 === undefined ? true : t1;
  const size = t2 === undefined ? "default" : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "agentBuilder.canvas.common"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t3);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef(null);
  const referenceIndex = useReferenceIndex();
  let t4;
  if ($[1] !== referenceIndex) {
    t4 = rehypeReferenceChips(referenceIndex);
    $[1] = referenceIndex;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = [t4];
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const rehypePlugins = t5;
  let t6;
  if ($[5] !== canEdit) {
    t6 = () => {
      if (!canEdit) {
        setOverflows(false);
        return;
      }
      const el = contentRef.current;
      if (el) {
        setOverflows(el.scrollHeight > COLLAPSED_MAX_HEIGHT + 4);
      }
    };
    $[5] = canEdit;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== canEdit || $[8] !== value) {
    t7 = [value, canEdit];
    $[7] = canEdit;
    $[8] = value;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  useEffect(t6, t7);
  const sizeClass = size === "large" ? "text-body-2" : "text-body-3";
  if (!value.trim()) {
    let t8;
    if ($[10] !== sizeClass) {
      t8 = cx(sizeClass, "text-tertiary");
      $[10] = sizeClass;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== placeholder || $[13] !== t8) {
      t9 = <span className={t8}>{placeholder}</span>;
      $[12] = placeholder;
      $[13] = t8;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    return t9;
  }
  const clamp = canEdit && !expanded;
  let t8;
  if ($[15] !== clamp || $[16] !== overflows) {
    t8 = clamp ? {
      maxHeight: COLLAPSED_MAX_HEIGHT,
      ...(overflows && {
        maskImage: FADE_MASK,
        WebkitMaskImage: FADE_MASK
      })
    } : undefined;
    $[15] = clamp;
    $[16] = overflows;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const collapsedStyle = t8;
  let t9;
  if ($[18] !== sizeClass) {
    t9 = cx("leading-snug text-secondary", sizeClass);
    $[18] = sizeClass;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== value) {
    t10 = instructionFencePreview(value);
    $[20] = value;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = {
      ...markdownComponents,
      pre: t10
    };
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== rehypePlugins || $[25] !== t11 || $[26] !== value) {
    t12 = <MarkdownText components={t11} rehypePlugins={rehypePlugins}>{value}</MarkdownText>;
    $[24] = rehypePlugins;
    $[25] = t11;
    $[26] = value;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== collapsedStyle || $[29] !== t12) {
    t13 = <div ref={contentRef} className="overflow-hidden" style={collapsedStyle}>{t12}</div>;
    $[28] = collapsedStyle;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== canEdit || $[32] !== expanded || $[33] !== overflows || $[34] !== t) {
    t14 = canEdit && overflows && <button type="button" onClick={event => {
      event.stopPropagation();
      setExpanded(_temp);
    }} className="mt-3 block text-label-3 font-medium text-accent hover:underline">{expanded ? t(_temp2) : t(_temp3)}</button>;
    $[31] = canEdit;
    $[32] = expanded;
    $[33] = overflows;
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14 || $[38] !== t9) {
    t15 = <div className={t9}>{t13}{t14}</div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};
export default ProsePreview;
function _temp(prev) {
  return !prev;
}
function _temp2($) {
  return $.showLess;
}
function _temp3($_0) {
  return $_0.showMore;
}